// Campaign (utm_*) labels on a signup link, e.g. a partner listing's
// https://dashboard.filespin.io/signup?utm_source=canva&utm_medium=referral&utm_campaign=plugins
//
// Read straight from the URL and sent with the signup (email: POST
// /api/v1/signup; Google / Microsoft: the SSO start URL, which keeps them in
// its server-side state through the IdP round-trip). The backend credits the
// new account in folk (Latest / First known platform...) and sends Plausible a
// "Trial Signup" event tagged with them. Nothing is stored in the browser.
//
// The same parameters and character rules as the website's forms Worker,
// trial form and Cal.com booking embed, so every channel reports the same
// labels.
//
// Ad measurement (Privacy Policy, 4 Nov 2026): the ad click IDs (gclid,
// gbraid, wbraid, li_fat_id) travel too, so an accepted signup can be
// reported to the ad platform it came from, unless the visitor opted out.
// A visitor from a paid ad sees an unticked opt-out box; a link from the
// website carries an opt-out already made there as fs_ad_opt_out=1.

export const UTM_PARAMS = [
  ['source', 'utm_source'],
  ['medium', 'utm_medium'],
  ['campaign', 'utm_campaign'],
  ['content', 'utm_content'],
  ['term', 'utm_term'],
  ['campaign_id', 'utm_id'],
] as const;

export const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'li_fat_id'] as const;
export const AD_OPT_OUT_PARAM = 'fs_ad_opt_out';

type ClickIdKey = (typeof CLICK_ID_PARAMS)[number];
export type ClickIds = Partial<Record<ClickIdKey, string>>;

export type SignupCampaign = Partial<Record<(typeof UTM_PARAMS)[number][0], string>> & {
  click_id?: ClickIds;
  ad_opt_out?: boolean;
};

const VALUE_RE = /^[\w .~:/+-]{1,200}$/;
const CLICK_ID_RE = /^[\w\-.~]{1,200}$/;
// The website's paid mediums (TrialForm PAID).
const PAID_MEDIUMS = ['cpc', 'ppc', 'paid', 'paid_social', 'paidsocial', 'display'];

// Only allowlisted, plain-label values; none at all without a utm_source.
export function utmParams(search: string): URLSearchParams {
  const query = new URLSearchParams(search);
  const out = new URLSearchParams();
  UTM_PARAMS.forEach(([, param]) => {
    const value = query.get(param)?.trim();
    if (value && VALUE_RE.test(value)) out.set(param, value);
  });
  return out.has('utm_source') ? out : new URLSearchParams();
}

export function clickIds(search: string): ClickIds {
  const query = new URLSearchParams(search);
  const out: ClickIds = {};
  CLICK_ID_PARAMS.forEach((param) => {
    const value = query.get(param)?.trim();
    if (value && CLICK_ID_RE.test(value)) out[param] = value;
  });
  return out;
}

// An opt-out already made on the website, carried on the link.
export function adOptOutFromSearch(search: string): boolean {
  const value = new URLSearchParams(search).get(AD_OPT_OUT_PARAM);
  return value === '1' || value === 'true';
}

// Labels plus click IDs, without the opt-out. An auto-tagged ad may carry
// only a click ID: it is attributed to Google (cpc) or LinkedIn (paid_social),
// as the backend and the website do.
function labelsFromSearch(search: string): SignupCampaign | undefined {
  const utms = utmParams(search);
  const campaign: SignupCampaign = {};
  UTM_PARAMS.forEach(([key, param]) => {
    const value = utms.get(param);
    if (value) campaign[key] = value;
  });
  const ids = clickIds(search);
  if (Object.keys(ids).length) {
    campaign.click_id = ids;
    if (!campaign.source && (ids.gclid || ids.gbraid || ids.wbraid)) {
      campaign.source = 'google';
      campaign.medium = campaign.medium || 'cpc';
    }
    if (!campaign.source && ids.li_fat_id) {
      campaign.source = 'linkedin';
      campaign.medium = campaign.medium || 'paid_social';
    }
  }
  return campaign.source ? campaign : undefined;
}

// A visit from a paid ad: the only visits shown the opt-out box.
export function isPaidVisit(search: string): boolean {
  const medium = labelsFromSearch(search)?.medium?.toLowerCase() || '';
  return PAID_MEDIUMS.includes(medium);
}

export function campaignFromSearch(search: string, adOptOut = false): SignupCampaign | undefined {
  const campaign = labelsFromSearch(search);
  if (!campaign) return undefined;
  return { ...campaign, ad_opt_out: adOptOut };
}

// The SSO start URL's attribution: utm_*, the click IDs and, when the
// visitor opted out, fs_ad_opt_out=1 (read by the backend's
// crm_tasks.campaign_from_utm).
export function attributionParams(search: string, adOptOut = false): URLSearchParams {
  const out = utmParams(search);
  Object.entries(clickIds(search)).forEach(([key, value]) => out.set(key, value as string));
  if (adOptOut) out.set(AD_OPT_OUT_PARAM, '1');
  return out;
}

// The page the visitor signed up on, without its query (the labels travel
// separately, and the query may hold things that are not ours to forward).
export function signupPage(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

// `path` with the current page's attribution appended (utm_*, click IDs and
// an opt-out made on the website), so a link between the login and signup
// pages does not drop it.
export function withUtm(path: string, search: string): string {
  const q = attributionParams(search, adOptOutFromSearch(search)).toString();
  return q ? `${path}${path.includes('?') ? '&' : '?'}${q}` : path;
}
